// src/pages/hr/HROnboarding.jsx
import React, { useState } from "react";
import { HR_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

const PANEL_LABEL = "HR Panel";

const CHECKLIST_ITEMS = [
  "Offer Letter Signed",
  "Aadhaar Submitted",
  "PAN Card Submitted",
  "Educational Certificates",
  "Bank Account Details",
  "Background Verification Initiated",
  "IT Account Created",
  "Laptop / Assets Allocated",
  "Orientation Completed",
  "Team Introduction Done",
];

const SAMPLE_JOINERS = [
  { name: "Ankit Verma", role: "Senior Developer", dept: "Engineering", joining: "2026-07-30", progress: 60, bgvStatus: "In Progress" },
  { name: "Divya Mehta", role: "QA Engineer", dept: "QA", joining: "2026-08-01", progress: 20, bgvStatus: "Not Started" },
  { name: "Siddharth Kumar", role: "HR Business Partner", dept: "HR", joining: "2026-08-05", progress: 80, bgvStatus: "Cleared" },
];

export default function HROnboarding() {
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState({});

  function toggleCheck(item) {
    setChecked((p) => ({ ...p, [item]: !p[item] }));
  }

  return (
    <Layout navItems={HR_NAV} panelLabel={PANEL_LABEL}>
      <style>{`
        .ob-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
        @media (max-width: 768px) { .ob-grid { grid-template-columns: 1fr; } }
        .ob-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.25rem 1.5rem; box-shadow: 0 1px 5px rgba(0,0,0,0.06); }
        .ob-card-title { font-size: 0.95rem; font-weight: 700; color: var(--text-primary, #111); margin: 0 0 1rem; }
        .ob-joiner { padding: 0.85rem 0; border-bottom: 1px solid var(--border, #f3f4f6); cursor: pointer; transition: background 0.15s; border-radius: 6px; padding-left: 0.5rem; }
        .ob-joiner:hover { background: var(--bg-secondary, #f9fafb); }
        .ob-joiner:last-child { border-bottom: none; }
        .ob-joiner-name { font-weight: 700; font-size: 0.9rem; color: var(--text-primary, #111); }
        .ob-joiner-meta { font-size: 0.75rem; color: var(--text-muted, #6b7280); margin-top: 2px; }
        .progress-bar { height: 6px; background: #e5e7eb; border-radius: 99px; overflow: hidden; margin-top: 6px; }
        .progress-fill { height: 100%; border-radius: 99px; background: linear-gradient(90deg, #1b4b31, #3da068); transition: width 0.4s; }
        .ob-bgv { display: inline-block; padding: 0.18rem 0.6rem; border-radius: 99px; font-size: 0.7rem; font-weight: 700; }
        .ob-bgv--cleared { background: #d1fae5; color: #065f46; }
        .ob-bgv--progress { background: #fef3c7; color: #92400e; }
        .ob-bgv--not { background: #f3f4f6; color: #6b7280; }
        .ob-check-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.5rem; }
        .ob-check-item { display: flex; align-items: center; gap: 0.75rem; padding: 0.5rem 0.65rem; border-radius: 7px; cursor: pointer; font-size: 0.85rem; transition: background 0.15s; }
        .ob-check-item:hover { background: var(--bg-secondary, #f9fafb); }
        .ob-check-box { width: 18px; height: 18px; border-radius: 4px; border: 2px solid #d1d5db; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.15s; }
        .ob-check-box--checked { background: #1b4b31; border-color: #1b4b31; color: #fff; font-size: 0.7rem; }
        .ob-check-label { color: var(--text-primary, #111); }
        .ob-check-label--done { text-decoration: line-through; color: var(--text-muted, #9ca3af); }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Employee Onboarding</h1>
          <p className="page-subtitle">Track new joiner onboarding progress, document collection, and BGV status</p>
        </div>
        <button className="btn-primary">+ Add New Joiner</button>
      </div>

      <div className="ob-grid">
        {/* New Joiners List */}
        <div className="ob-card">
          <div className="ob-card-title">🆕 Upcoming New Joiners ({SAMPLE_JOINERS.length})</div>
          {SAMPLE_JOINERS.map((j) => (
            <div className="ob-joiner" key={j.name} onClick={() => setSelected(j)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div>
                  <div className="ob-joiner-name">{j.name}</div>
                  <div className="ob-joiner-meta">{j.role} · {j.dept} · Joining: {j.joining}</div>
                </div>
                <span className={`ob-bgv ${j.bgvStatus === "Cleared" ? "ob-bgv--cleared" : j.bgvStatus === "In Progress" ? "ob-bgv--progress" : "ob-bgv--not"}`}>
                  BGV: {j.bgvStatus}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                <div className="progress-bar" style={{ flex: 1 }}>
                  <div className="progress-fill" style={{ width: `${j.progress}%` }} />
                </div>
                <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#1b4b31" }}>{j.progress}%</span>
              </div>
            </div>
          ))}
        </div>

        {/* Onboarding Checklist */}
        <div className="ob-card">
          <div className="ob-card-title">
            ✅ Onboarding Checklist{selected ? ` — ${selected.name}` : " (Select a Joiner)"}
          </div>
          {!selected ? (
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Click on a new joiner on the left to see their checklist.</p>
          ) : (
            <ul className="ob-check-list">
              {CHECKLIST_ITEMS.map((item) => (
                <li key={item} className="ob-check-item" onClick={() => toggleCheck(`${selected.name}_${item}`)}>
                  <div className={`ob-check-box ${checked[`${selected.name}_${item}`] ? "ob-check-box--checked" : ""}`}>
                    {checked[`${selected.name}_${item}`] && "✓"}
                  </div>
                  <span className={`ob-check-label ${checked[`${selected.name}_${item}`] ? "ob-check-label--done" : ""}`}>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Probation Tracker */}
      <div className="ob-card" style={{ marginTop: "1.5rem" }}>
        <div className="ob-card-title">⏳ Probation Tracker</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.84rem" }}>
          <thead>
            <tr>
              {["Employee", "Role", "Joining Date", "Probation End", "Status"].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "0.6rem 0.75rem", background: "var(--bg-secondary, #f9fafb)", borderBottom: "2px solid var(--border, #e5e7eb)", fontSize: "0.75rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SAMPLE_JOINERS.map((j) => {
              const probEnd = new Date(j.joining);
              probEnd.setMonth(probEnd.getMonth() + 6);
              return (
                <tr key={j.name}>
                  <td style={{ padding: "0.65rem 0.75rem", fontWeight: 600, borderBottom: "1px solid var(--border, #f3f4f6)" }}>{j.name}</td>
                  <td style={{ padding: "0.65rem 0.75rem", borderBottom: "1px solid var(--border, #f3f4f6)" }}>{j.role}</td>
                  <td style={{ padding: "0.65rem 0.75rem", borderBottom: "1px solid var(--border, #f3f4f6)" }}>{j.joining}</td>
                  <td style={{ padding: "0.65rem 0.75rem", borderBottom: "1px solid var(--border, #f3f4f6)" }}>{probEnd.toISOString().split("T")[0]}</td>
                  <td style={{ padding: "0.65rem 0.75rem", borderBottom: "1px solid var(--border, #f3f4f6)" }}>
                    <span style={{ background: "#fef3c7", color: "#92400e", padding: "0.18rem 0.6rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700 }}>On Probation</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
